import type { QueryClient } from '@tanstack/react-query';
import type { TrackMethod } from '@shore/types';
import { TRACK_IDENTIFY, TRACK_PAGE, NOOP } from '../constants';
import { keys as queryKeys } from '../queries';
import createDebug from '../utils/debug';
import type { ErrorLogger } from '../shared/types';

const debug = createDebug('track-method');

export interface AnalyticsInstance {
  track: (eventName: string, payload?: Record<string, unknown>) => void;
  identify: (id: string, payload?: Record<string, unknown>) => void;
  page: (name: string, payload?: Record<string, unknown>) => void;
}

export type GetAnalytics = () => Partial<AnalyticsInstance> | null | undefined;

interface IdentifyPayload {
  id: string;
  data?: Record<string, unknown>;
}

interface PagePayload {
  name: string;
  properties?: Record<string, unknown>;
}

const defaultInstance: AnalyticsInstance = {
  track: (...args) => debug('track', ...args),
  identify: (...args) => debug(TRACK_IDENTIFY, ...args),
  page: (...args) => debug(TRACK_PAGE, ...args),
};

export default function createTrackMethod(
  queryClient: QueryClient,
  getAnalytics: GetAnalytics = NOOP as GetAnalytics,
  errorLogger?: ErrorLogger
): TrackMethod {
  return (eventName, eventPayload) => {
    // Returns a stand-in that only throws when the missing analytics
    // method is actually invoked for the current event.
    const throwError =
      (error: string) =>
      (..._args: unknown[]): never => {
        throw new Error(error);
      };

    const {
      track = throwError('track method missing'),
      identify = throwError('identify method missing'),
      page = throwError('page method missing'),
    } = getAnalytics() || defaultInstance;

    function addSessionMetadata(payload?: Record<string, unknown>) {
      const trackingMetadata =
        queryClient.getQueryData<Record<string, unknown>>(
          queryKeys.trackingSessionMetadata
        ) || {};

      return {
        ...payload,
        ...trackingMetadata,
      };
    }

    switch (eventName) {
      case TRACK_IDENTIFY: {
        const payload = eventPayload as IdentifyPayload;
        identify(payload.id, addSessionMetadata(payload.data));
        return;
      }

      case TRACK_PAGE: {
        const payload = eventPayload as PagePayload;
        page(payload.name, addSessionMetadata(payload.properties));
        return;
      }

      default:
        if (eventName) {
          /* this for investigation purpose only
           * should be reverted after ticket https://group-one.atlassian.net/browse/AP-1643
           * is resolved
           * PR is: https://github.com/shore-gmbh/app-shell/pull/new/AP-1643-check-mixpanel-data
           */

          if (eventName === 'services_edit_course_save_clicked') {
            const payloadWithMetadata = addSessionMetadata(
              eventPayload as Record<string, unknown>
            );

            if (!payloadWithMetadata.session_role) {
              errorLogger?.log(
                new Error('Session role not found in tracking payload'),
                {
                  payloadWithMetaData: JSON.stringify(payloadWithMetadata),
                }
              );
            }

            track(eventName, payloadWithMetadata);
          } else {
            track(
              eventName,
              addSessionMetadata(eventPayload as Record<string, unknown>)
            );
          }
        }
    }
  };
}
